import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Sparkles,
  Palette,
  CreditCard,
  Layers,
  Users,
  BarChart3,
  Globe,
  Check,
  X,
  Zap,
  Settings,
  ShoppingBag,
} from 'lucide-react';
import Container from '../components/layout/Container';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import Badge from '../components/common/Badge';

// Landing page institucional do Reviter (sem tenant resolvido). Layout
// inspirado em SaaS B2B (Nuvemshop, Shopify): hero + trust strip + features
// grid + 3 passos "como funciona" + planos + CTA final.

const FEATURES = [
  {
    icon: Palette,
    title: 'Whitelabel completo',
    desc: 'Cor, logo, domínio próprio, layout — sua marca em primeiro plano. Cada loja com identidade única.',
  },
  {
    icon: CreditCard,
    title: 'Pagamentos integrados',
    desc: 'Asaas e Pagar.me prontos para uso. PIX, boleto, cartão e split automático com revendedores.',
  },
  {
    icon: Layers,
    title: 'Multi-loja',
    desc: 'Uma conta gerencia diversas lojas. Ideal pra grupos com várias marcas ou marketplaces de nicho.',
  },
  {
    icon: Users,
    title: 'Rede de revendedores',
    desc: 'Programas de afiliados com comissão configurável. Cada revendedor tem painel próprio e link rastreável.',
  },
  {
    icon: BarChart3,
    title: 'Painel completo',
    desc: 'Pedidos, estoque, KPIs, leads, campanhas. Tudo no mesmo lugar com permissões granulares por papel.',
  },
  {
    icon: Globe,
    title: 'Domínio próprio + SSL',
    desc: 'Subdomínio gratuito ou aponte seu domínio personalizado. SSL automático em todos os planos.',
  },
];

const STEPS = [
  {
    n: 1,
    icon: Zap,
    title: 'Cadastre sua loja',
    desc: 'Em menos de 60 segundos você tem uma loja ativa, com endereço gratuito loja.reviter.com.br.',
  },
  {
    n: 2,
    icon: Settings,
    title: 'Personalize',
    desc: 'Cores, logo, descrição e categorias — ajuste do seu jeito. Quer domínio próprio? Aponte e ative.',
  },
  {
    n: 3,
    icon: ShoppingBag,
    title: 'Comece a vender',
    desc: 'Cadastre produtos, configure pagamentos, convide revendedores. Acompanhe cada pedido em tempo real.',
  },
];

// Limite/feature gate dos planos. Os valores numéricos (30 produtos, 4,9%)
// são placeholders comerciais — ajustar conforme política de billing final.
const PLAN_FEE_DEFAULT = '4,9% por venda';
const PLAN_FEE_CUSTOM = 'Taxa negociada';

const PLAN_DEFAULT_FEATURES = [
  'Subdomínio gratuito loja.reviter.com.br',
  'Whitelabel: cores, logo e descrição da loja',
  'Catálogo de até 30 produtos ativos',
  '1 modo de loja (vendedor, revenda ou catálogo WhatsApp)',
  '1 usuário owner',
  'Pagamentos via Asaas e Pagar.me',
  'Pedidos com numeração sequencial e histórico',
  'Login por email, CPF ou celular',
  'Suporte por email (resposta em até 48h)',
];

// Itens NÃO incluídos no plano Default — mostrados riscados pra deixar
// claro o que entra só no Custom.
const PLAN_DEFAULT_NOT_INCLUDED = [
  'Domínio próprio com SSL',
  'Programa de revendedores com split',
  'Cupons e campanhas',
  'Cálculo de frete (PAC, Sedex, transportadora)',
  'Múltiplos usuários por loja',
];

const PLAN_CUSTOM_FEATURES = [
  'Catálogo ilimitado (produtos, variações, categorias)',
  'Domínio próprio com SSL',
  'Múltiplos usuários por loja com papéis (staff, reseller, cliente)',
  'Programa de revendedores com comissão configurável',
  'Cupons e campanhas (% / valor fixo / frete grátis)',
  'Cálculo de frete com múltiplas opções',
  'Notificações em tempo real e captação de leads via WhatsApp',
  'Front-end totalmente custom (opcional)',
  'Integrações sob medida (ERP, CRM, gateways adicionais)',
  'Onboarding assistido + migração de catálogo',
  'SLA e suporte dedicado',
];

const ReviterHomePage = () => {
  return (
    <div>
      {/* Hero — calibrado pra caber em uma viewport sem scroll em md+.
          Header (~60px) + section min-h-[calc(100vh-60px)] = janela cheia.
          Mobile não força altura pra evitar comprimir conteúdo. */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary via-primary-dark to-secondary text-white md:min-h-[calc(100vh-60px)] md:flex md:items-center">
        <div
          className="absolute inset-0 opacity-30"
          aria-hidden="true"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.15), transparent 40%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.10), transparent 40%)',
          }}
        />
        <Container className="!py-8 md:!py-10">
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <Badge color="primary" variant="soft" className="!bg-white/15 !text-white mb-4">
                <Sparkles size={14} /> Plataforma whitelabel multi-tenant
              </Badge>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight mb-4 leading-tight">
                Crie sua{' '}
                <span className="bg-gradient-to-br from-white to-primary-light bg-clip-text text-transparent">
                  loja online
                </span>{' '}
                em minutos.
              </h1>
              <p className="text-base md:text-lg text-white/90 mb-6 max-w-2xl">
                Plataforma completa pra fornecedores e revendedores: catálogo, pedidos,
                pagamentos integrados e split automático. Sem código, com a sua marca.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/cadastrar">
                  <Button size="lg" variant="accent" rightIcon={<ArrowRight size={18} />}>
                    Criar grátis
                  </Button>
                </Link>
                <a href="#planos">
                  <Button
                    size="lg"
                    variant="outline"
                    className="!border-white/40 !text-white hover:!bg-white/10"
                  >
                    Ver planos
                  </Button>
                </a>
              </div>
            </div>

            {/* Mockup ilustrativo do storefront — cards estilizados pra dar a ideia
                sem precisar de screenshot estática. */}
            <div className="lg:col-span-5 hidden md:block">
              <div className="relative max-w-md mx-auto">
                <div className="absolute inset-0 -translate-x-3 translate-y-3 bg-white/10 rounded-2xl rotate-2" />
                <div className="relative bg-white rounded-2xl shadow-2xl p-4 rotate-[-1deg] hover:rotate-0 transition-transform duration-500">
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-app-border">
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-primary to-secondary text-white font-extrabold text-sm flex items-center justify-center">L</div>
                    <span className="font-bold text-app-text text-sm">Sua Loja</span>
                    <div className="ml-auto flex gap-1">
                      <div className="w-2 h-2 rounded-full bg-error" />
                      <div className="w-2 h-2 rounded-full bg-warning" />
                      <div className="w-2 h-2 rounded-full bg-success" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="bg-app-surface rounded-lg overflow-hidden">
                        <div className="aspect-square bg-gradient-to-br from-primary/20 to-secondary/20" />
                        <div className="p-2">
                          <div className="h-2 bg-app-border rounded w-2/3 mb-1.5" />
                          <div className="h-3 bg-primary/70 rounded w-1/2" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Trust strip */}
      <section className="bg-app-surface border-y border-app-border">
        <Container>
          <div className="py-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <p className="text-2xl md:text-3xl font-extrabold text-primary">100+</p>
              <p className="text-xs text-app-text-secondary uppercase tracking-wider mt-1">Lojas ativas</p>
            </div>
            <div>
              <p className="text-2xl md:text-3xl font-extrabold text-primary">99.9%</p>
              <p className="text-xs text-app-text-secondary uppercase tracking-wider mt-1">Uptime</p>
            </div>
            <div>
              <p className="text-2xl md:text-3xl font-extrabold text-primary">24/7</p>
              <p className="text-xs text-app-text-secondary uppercase tracking-wider mt-1">Plataforma online</p>
            </div>
            <div>
              <p className="text-2xl md:text-3xl font-extrabold text-primary">2 gateways</p>
              <p className="text-xs text-app-text-secondary uppercase tracking-wider mt-1">Pagamentos</p>
            </div>
          </div>
        </Container>
      </section>

      {/* Features */}
      <section id="recursos" className="py-16 md:py-20 bg-white scroll-mt-16">
        <Container>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-app-text mb-3">
              Tudo o que sua loja precisa, num lugar só
            </h2>
            <p className="text-app-text-secondary">
              Da configuração visual ao pagamento split com revendedores — sem precisar
              encadear ferramentas separadas.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <Card key={title} className="p-6 hover:shadow-lg transition-shadow border border-app-border">
                <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4">
                  <Icon size={22} />
                </div>
                <h3 className="text-lg font-bold text-app-text mb-2">{title}</h3>
                <p className="text-sm text-app-text-secondary leading-relaxed">{desc}</p>
              </Card>
            ))}
          </div>
        </Container>
      </section>

      {/* Como funciona */}
      <section id="como-funciona" className="py-16 md:py-20 bg-app-surface scroll-mt-16">
        <Container>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-app-text mb-3">
              Como funciona
            </h2>
            <p className="text-app-text-secondary">
              Em 3 passos sua loja está pronta pra receber pedidos.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
            {/* Linha conectando os passos no desktop */}
            <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 bg-gradient-to-r from-primary via-secondary to-primary opacity-30" aria-hidden="true" />
            {STEPS.map(({ n, icon: Icon, title, desc }) => (
              <div key={n} className="relative bg-white rounded-xl border border-app-border p-6 text-center">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-primary text-white font-bold flex items-center justify-center shadow">
                  {n}
                </div>
                <div className="w-14 h-14 mx-auto mb-4 mt-2 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                  <Icon size={24} />
                </div>
                <h3 className="text-lg font-bold text-app-text mb-2">{title}</h3>
                <p className="text-sm text-app-text-secondary">{desc}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Planos */}
      <section id="planos" className="py-16 md:py-20 bg-white scroll-mt-16">
        <Container>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-app-text mb-3">
              Planos que crescem com você
            </h2>
            <p className="text-app-text-secondary">
              Comece grátis pra validar a ideia e evolua pro Custom quando o volume crescer.
              Sem mensalidade no Default — você só paga uma taxa por venda concluída.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <Card className="p-8 border-2 border-app-border flex flex-col">
              <h3 className="text-xl font-bold text-app-text mb-1">Plano Default</h3>
              <p className="text-sm text-app-text-secondary mb-4">Pra validar e começar a vender</p>
              <div className="mb-2">
                <span className="text-4xl font-extrabold text-app-text">R$ 0</span>
                <span className="text-sm text-app-text-secondary ml-1">/ mês</span>
              </div>
              <p className="text-sm text-app-text-secondary mb-2">
                + <strong className="text-app-text">{PLAN_FEE_DEFAULT}</strong> realizada
                <span className="text-xs"> (descontada no split)</span>
              </p>
              <div className="mb-6 p-3 rounded-md bg-warning/10 border border-warning/30 text-xs text-app-text-secondary">
                Plano gratuito tem limites: até 30 produtos, 1 usuário e features básicas.
                Use pra validar — quando crescer, migre pro Custom.
              </div>
              <ul className="space-y-2 mb-4 flex-1">
                {PLAN_DEFAULT_FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-app-text">
                    <Check size={16} className="text-success shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
              {PLAN_DEFAULT_NOT_INCLUDED.length > 0 && (
                <ul className="space-y-2 mb-8 pt-3 border-t border-app-border">
                  {PLAN_DEFAULT_NOT_INCLUDED.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-app-text-secondary line-through">
                      <X size={16} className="text-app-text-secondary shrink-0 mt-0.5 no-underline" />
                      {f}
                    </li>
                  ))}
                </ul>
              )}
              <Link to="/cadastrar">
                <Button fullWidth>Criar minha loja grátis</Button>
              </Link>
            </Card>

            <Card className="p-8 border-2 border-primary relative flex flex-col bg-gradient-to-br from-white to-primary/5">
              <Badge color="primary" variant="solid" size="sm" className="absolute -top-3 left-1/2 -translate-x-1/2">
                Recomendado
              </Badge>
              <h3 className="text-xl font-bold text-app-text mb-1">Plano Custom</h3>
              <p className="text-sm text-app-text-secondary mb-4">Pra operações que vão escalar</p>
              <div className="mb-2">
                <span className="text-4xl font-extrabold text-app-text">Sob consulta</span>
              </div>
              <p className="text-sm text-app-text-secondary mb-6">
                Mensalidade fixa + <strong className="text-app-text">{PLAN_FEE_CUSTOM}</strong> por venda
              </p>
              <ul className="space-y-2 mb-8 flex-1">
                {PLAN_CUSTOM_FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-app-text">
                    <Check size={16} className="text-primary shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
              <a href="mailto:contato@reviter.com.br?subject=Plano%20Custom">
                <Button fullWidth variant="primary">Falar com vendas</Button>
              </a>
            </Card>
          </div>

          <p className="text-center text-xs text-app-text-secondary mt-6 max-w-2xl mx-auto">
            * A taxa por venda é descontada automaticamente no split do gateway de pagamento e
            cobrada apenas em pedidos concluídos. Pedidos cancelados ou estornados não geram cobrança.
            Limites e taxas podem ser revisados a qualquer momento — consulte os termos vigentes.
          </p>
        </Container>
      </section>

      {/* CTA Final */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-secondary-dark via-primary-dark to-primary text-white relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          aria-hidden="true"
          style={{
            backgroundImage:
              'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.18), transparent 45%), radial-gradient(circle at 70% 70%, rgba(255,255,255,0.10), transparent 40%)',
          }}
        />
        <Container>
          <div className="relative z-10 max-w-3xl mx-auto text-center">
            <Badge color="primary" variant="soft" className="!bg-white/15 !text-white mb-5">
              <Sparkles size={14} /> Comece em minutos
            </Badge>
            <h2 className="text-3xl md:text-5xl font-extrabold mb-4 tracking-tight">
              Pronto pra colocar sua loja no ar?
            </h2>
            <p className="text-lg md:text-xl text-white/90 mb-8 max-w-xl mx-auto">
              Cadastre-se grátis, configure sua marca e cadastre os primeiros produtos
              hoje mesmo. Sem cartão, sem fidelidade.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-10">
              <Link to="/cadastrar">
                <Button size="lg" variant="accent" rightIcon={<ArrowRight size={18} />}>
                  Criar conta grátis
                </Button>
              </Link>
              <a href="mailto:contato@reviter.com.br?subject=Quero%20conhecer%20o%20Reviter">
                <Button
                  size="lg"
                  variant="outline"
                  className="!border-white/40 !text-white hover:!bg-white/10"
                >
                  Falar com vendas
                </Button>
              </a>
            </div>

            <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl mx-auto text-sm text-white/85">
              <li className="flex items-center justify-center gap-2">
                <Check size={16} className="text-success" />
                Sem cartão de crédito
              </li>
              <li className="flex items-center justify-center gap-2">
                <Check size={16} className="text-success" />
                Sem fidelidade
              </li>
              <li className="flex items-center justify-center gap-2">
                <Check size={16} className="text-success" />
                Sua marca, seu domínio
              </li>
            </ul>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default ReviterHomePage;
